import { useEditorState, type Editor } from '@tiptap/react';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { linkedSessionContextAtom } from '@/atoms/chatAtoms';
import { usePaywallStates } from '@/atoms/paywallAtoms';
import { useMutationChatSuggestions } from '@/components/chat/hooks/mutations/useMutationChatSuggestions';
import { useChatSessionId } from '@/components/chat/hooks/useChatSessionId';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useQueryUser } from '@heidi/workspace/data';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { isChatSuggestionsApiAvailable } from '@/utils/backendSurfaces';

import type { ChatSuggestion } from '@/components/chat/types/types';

const MAX_INPUT_LENGTH = 100;
const DEBOUNCE_DELAY_MS = 800;
const MIN_WORD_COUNT = 1;

const countWords = (text: string): number => {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
};

interface UseChatSuggestionsOptions {
  editor: Editor | null;
  enabled?: boolean;
  /** Callback to submit the message (called when clicking a suggestion) */
  onSend?: (message: string) => void;
}

interface UseChatSuggestionsReturn {
  suggestions: ChatSuggestion[];
  handleSuggestionClick: (index: number) => void;
  isSuggestionsPending: boolean;
  hasText: boolean;
  hasEvidencePro: boolean;
  /** Whether to show suggestions (has suggestions and input is short enough) */
  shouldShowSuggestions: boolean;
  /**
   * True while the list on screen is about to be replaced: a request for the
   * current input is in flight, or the debounce is still counting down on text
   * that has diverged from the query the visible suggestions came from.
   *
   * Distinct from the raw `isSuggestionsPending` mutation flag, and the reason
   * it exists: suggestions come from a MUTATION, so `data` is cleared the moment
   * the next request starts. A host keying visibility on `suggestions.length`
   * alone therefore unmounts the sheet on every keystroke that reaches the
   * backend and remounts it when the answer lands. This says "keep the surface,
   * the contents are coming" — false once there is genuinely nothing to wait
   * for, so an empty or over-long input still closes it.
   */
  isSuggestionsRefreshing: boolean;
  /** Current editor input text — used to highlight matching prefix in suggestions */
  searchText: string;
  /** Trigger a suggestion search immediately (bypasses debounce). Use after programmatic input changes like populate. */
  searchImmediately: (text: string) => void;
}

export const useChatSuggestions = ({
  editor,
  enabled: isEnabledByHost = true,
  onSend,
}: UseChatSuggestionsOptions): UseChatSuggestionsReturn => {
  // `POST /chat/suggestions` stays in ml-scribe's cloud-only chat router, so on
  // an on-prem build there is no route to answer the type-ahead. Folded into
  // `enabled` here — the one hook both suggestion surfaces call — so the
  // debounced search, the immediate search and the editor subscription all
  // stand down together and no keystroke reaches the network.
  const enabled = isEnabledByHost && isChatSuggestionsApiAvailable;
  const { hasEvidencePro } = useEvidenceBillingInfo();
  const { toggleUpgradeEvidenceProModal } = usePaywallStates();

  // Get user specialty
  const { data: specialtyId } = useQueryUser((data) => data?.specialty_id);
  const { data: specialties } = useQuerySpecialties((d) => d.specialties);
  const specialtyGroupId = specialtyId
    ? specialties?.find((s) => s.id === specialtyId)?.id
    : undefined;

  // Resolve the active session id for BE span tagging (heidi.session_id, EVI-1540).
  // Mirrors Chat.tsx:701 / ChatInput.tsx:693: prefer the in-session route id,
  // then fall back to a user-linked session (out-of-session Evidence). Null
  // when neither applies, in which case the BE leaves the span attribute unset.
  const routeSessionId = useChatSessionId();
  const linkedSession = useAtomValue(linkedSessionContextAtom);
  const sessionId = routeSessionId ?? linkedSession?.id ?? null;

  // Get editor text
  const searchTextRaw = useEditorState({
    editor,
    selector: ({ editor: ed }) => ed?.getText()?.trim() ?? '',
  });

  // Ensure searchText is always a string
  const searchText = searchTextRaw ?? '';
  const hasText = searchText.length > 0;

  const {
    data: { suggestions = [] } = {},
    isPending: isSuggestionsPending,
    mutateAsync: createSuggestions,
    reset: resetSuggestions,
  } = useMutationChatSuggestions();

  // Track which query the current suggestions are for
  // This prevents showing stale suggestions when typing a new query
  const lastQueryRef = useRef<string>('');

  const hasMoreThan100Characters = searchText.length > MAX_INPUT_LENGTH;

  // Debounced search function - only triggers after MIN_WORD_COUNT words
  const debouncedSearch = useDebouncedCallback(async (text: string) => {
    if (!enabled) return;

    const wordCount = countWords(text);
    const hasEnoughWords = wordCount >= MIN_WORD_COUNT;

    if (hasEnoughWords && !hasMoreThan100Characters) {
      // Store the query we're fetching suggestions for
      lastQueryRef.current = text;
      try {
        await createSuggestions({
          text,
          specialtyGroupId,
          sessionId,
        });
      } catch {
        resetSuggestions();
      }
    }
    if (!hasEnoughWords) {
      lastQueryRef.current = '';
      resetSuggestions();
    }
  }, DEBOUNCE_DELAY_MS);

  // Handle editor updates
  const handleUpdate = useCallback(() => {
    const text = editor?.getText()?.trim() ?? '';
    void debouncedSearch(text);
  }, [editor, debouncedSearch]);

  // Immediate search — bypasses debounce and MIN_WORD_COUNT.
  // Used after programmatic input changes (e.g. populate from verb suggestion)
  // where we know the input is a valid clinical stub, even if it's a single word
  // like "Calculate" or "Compare".
  const searchImmediately = useCallback(
    (text: string) => {
      if (!enabled) return;
      if (text.trim().length > 0 && text.length <= MAX_INPUT_LENGTH) {
        // The same editor update that populated the starter also schedules
        // `debouncedSearch`. Cancel it so a click is one `/chat/suggestions`
        // request, not two.
        debouncedSearch.cancel?.();
        // Starters are stored with a trailing space ("Calculate "). searchText
        // is always trimmed, so lastQuery must be too or shouldShowSuggestions
        // (`searchText.startsWith(lastQuery)`) is false and the sheet empties.
        lastQueryRef.current = text.trim();
        void Promise.resolve(
          createSuggestions({ text, specialtyGroupId, sessionId })
        ).catch(() => {
          resetSuggestions();
        });
      }
    },
    [
      enabled,
      createSuggestions,
      resetSuggestions,
      specialtyGroupId,
      sessionId,
      debouncedSearch,
    ]
  );

  // Subscribe to editor updates
  useEffect(() => {
    if (!editor || !enabled) return;

    editor.on('update', handleUpdate);

    return () => {
      editor?.off?.('update', handleUpdate);
    };
  }, [editor, enabled, handleUpdate]);

  // Handle clicking a suggestion - now submits the message
  const handleSuggestionClick = useCallback(
    (index: number) => {
      const suggestion = suggestions.at(index);
      if (!suggestion) return;

      // If it's a Pro suggestion and user doesn't have Pro, show paywall
      if (suggestion.type === 'pro' && !hasEvidencePro) {
        toggleUpgradeEvidenceProModal(true);
        return;
      }

      // Submit the suggestion directly
      if (onSend) {
        onSend(suggestion.content);
        // Clear suggestions after sending
        resetSuggestions();
      }
    },
    [suggestions, hasEvidencePro, toggleUpgradeEvidenceProModal, onSend, resetSuggestions]
  );

  // Determine if we should show suggestions
  // Only show if current text starts with the query that generated them (prevents stale suggestions)
  // Note: lastQueryRef.current is intentionally read inside useMemo without being in deps.
  // This is safe because searchText (which IS in deps) triggers recalculation, and the
  // startsWith comparison is evaluated freshly each time. The ref stores the query that
  // generated current suggestions, preventing stale suggestions from showing.
  const shouldShowSuggestions = useMemo(() => {
    const hasEnoughWords = countWords(searchText) >= MIN_WORD_COUNT;
    const lastQuery = lastQueryRef.current.trim();
    const suggestionsAreRelevant =
      lastQuery !== '' && searchText.startsWith(lastQuery);
    // Show suggestions if the user typed enough words OR if we already fetched
    // relevant suggestions via searchImmediately (e.g. single-word verb starters
    // like "Calculate" or "Compare").
    return (
      enabled &&
      (hasEnoughWords || suggestionsAreRelevant) &&
      !hasMoreThan100Characters &&
      suggestions.length > 0 &&
      suggestionsAreRelevant
    );
  }, [enabled, searchText, hasMoreThan100Characters, suggestions.length]);

  // Deliberately not memoised: this reads lastQueryRef, which a debounced search
  // can move without changing any of the values a dependency array could watch.
  // The work is a word count on at most 100 characters.
  //
  // lastQueryRef is trimmed for the comparison so a leftover trailing space
  // (populate starters historically stored "What is the dosing of ") cannot
  // make this report a refresh that never ends against trimmed searchText.
  const isEligibleForSuggestions =
    enabled && countWords(searchText) >= MIN_WORD_COUNT && !hasMoreThan100Characters;
  const isSuggestionsRefreshing =
    isEligibleForSuggestions &&
    (isSuggestionsPending || searchText !== lastQueryRef.current.trim());

  return {
    suggestions,
    handleSuggestionClick,
    isSuggestionsPending,
    hasText,
    hasEvidencePro,
    shouldShowSuggestions,
    isSuggestionsRefreshing,
    searchText,
    searchImmediately,
  };
};
